import React, { useState, useEffect } from "react";
import { MdDelete, MdOutlineAddLink } from "react-icons/md";
import { HiOutlinePlus } from "react-icons/hi";
import InputField from "../../common/InputField";
import Model from "../../common/Model";
import EntityAddModal from "../../common/EntityAddModal";
import { FormHeader, FormFooter } from "../../common/Shared";
import Table from "../../common/Table";
import { materialOutward, materialInward } from "../../utlis/apiService";
import { notify } from "../../common/Toast";

const MaterialOutwardForm = ({
  formData: initialData,
  isEditing,
  isSaving,
  onSave,
  onCancel,
  isCustomerData,
  isRawMaterialData,
  isUnitData,
  userId,
}) => {
  const [modalType, setModalType] = useState(null);
  const [formFields, setFormFields] = useState(initialData);
  const [isSkuData, setIsSkuData] = useState([]);

  useEffect(() => {
    setFormFields(initialData);
  }, [initialData]);

  useEffect(() => {
    const fetchSKUs = async () => {
      try {
        const response = await materialInward.getAllSKU();
        // Assuming response.data contains the list of SKUs
        setIsSkuData(response.data || response || []);
      } catch (error) {
        console.error("Error fetching SKUs:", error);
      }
    };
    fetchSKUs();
  }, []);

  const handleChange = (name, value) => {
    setFormFields((prev) => ({ ...prev, [name]: value }));
  };

  const calculateTotals = (details) => {
    let totalAmount = 0;
    let totalGSTAmount = 0;
    let totalFinalAmount = 0;

    details.forEach(item => {
      totalAmount += parseFloat(item.amount || 0);
      totalGSTAmount += parseFloat(item.gsT_Amount || 0);
      totalFinalAmount += parseFloat(item.final_Amount || 0);
    });

    setFormFields(prev => ({
      ...prev,
      details,
      total_Amount: parseFloat(totalAmount.toFixed(2)),
      total_GST_Amount: parseFloat(totalGSTAmount.toFixed(2)),
      total_Final_Amount: parseFloat(totalFinalAmount.toFixed(2))
    }));
  };

  const handleDetailChange = (index, field, value) => {
    const newDetails = [...formFields.details];
    newDetails[index][field] = value;

    // Recalculate amounts if relevant fields change
    if (["quantity", "rate", "gsT_Per"].includes(field)) {
      const qty = parseFloat(newDetails[index].quantity || 0);
      const rate = parseFloat(newDetails[index].rate || 0);
      const gstPer = parseFloat(newDetails[index].gsT_Per || 0);

      const amount = qty * rate;
      const gstAmount = amount * (gstPer / 100);
      const finalAmount = amount + gstAmount;

      newDetails[index].amount = parseFloat(amount.toFixed(2));
      newDetails[index].gsT_Amount = parseFloat(gstAmount.toFixed(2));
      newDetails[index].final_Amount = parseFloat(finalAmount.toFixed(2));
    }

    calculateTotals(newDetails);
  };

  const addDetailItem = () => {
    const newItem = {
      trn_No: 0,
      inward_No: 0,
      outward_No: 0,
      material_Id: 0,
      quantity: "",
      unit_Id: "",
      rate: "",
      amount: 0,
      gsT_Per: 18.00, // Default to 18%
      gsT_Amount: 0,
      final_Amount: 0,
      created_By: userId,
    };
    calculateTotals([...(formFields.details || []), newItem]);
  };

  const removeDetailItem = async (index) => {
    const itemToRemove = formFields.details[index];

    // If it's an existing item from the database, call the API to delete
    if (itemToRemove.trn_No > 0) {
      try {
        await materialOutward.deleteItem(itemToRemove.trn_No);
        notify("Detail item removed successfully", "success");
      } catch (error) {
        notify("Failed to remove detail item", "error");
        return; // Exit if API deletion fails
      }
    }

    if (formFields.details.length <= 1) {
      // Clear the single row instead of removing it to keep the grid active
      const clearedRow = {
        trn_No: 0,
        inward_No: 0,
        outward_No: 0,
        material_Id: 0,
        quantity: "",
        unit_Id: "",
        rate: "",
        amount: 0,
        gsT_Per: 18.00,
        gsT_Amount: 0,
        final_Amount: 0,
        created_By: userId,
        modified_By: userId
      };
      calculateTotals([clearedRow]);
    } else {
      const newDetails = formFields.details.filter((_, i) => i !== index);
      calculateTotals(newDetails);
    }
  };

  const handleSubmit = (e, resetOnly = false) => {
    if (e) e.preventDefault();

    if (!formFields.details || formFields.details.length === 0) {
      notify("Please add at least one material item", "warning");
      return;
    }

    // Add master totals to payload
    const payload = {
      ...formFields,
    };

    onSave(payload, resetOnly);
  };

  const customFooter = (
    <tr>
      <td colSpan="8" className="px-4 py-3">
        <div className="flex justify-end items-center gap-8">
          <div className="flex items-center gap-2 text-[11px] font-bold text-gray-600 uppercase">
            <span>Total Amount:</span>
            <span className="text-gray-900">{formFields.total_Amount?.toFixed(2) || "0.00"}</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-bold text-gray-600 uppercase">
            <span>Total GST:</span>
            <span className="text-gray-900">{formFields.total_GST_Amount?.toFixed(2) || "0.00"}</span>
          </div>
          <div className="flex items-center gap-2 text-[12px] font-black text-primary uppercase pl-6 border-l border-gray-200">
            <span>Final Amount:</span>
            <span className="text-[14px]">{formFields.total_Final_Amount?.toFixed(2) || "0.00"}</span>
          </div>
        </div>
      </td>
    </tr>
  );

  const detailColumns = [
    {
      key: "inward_No",
      label: "SKU No",
      w: "w-40",
      className: "align-top",
      render: (val, row, index) => (
        <InputField
          type="select"
          value={val}
          onChange={(v) => handleDetailChange(index, "inward_No", v)}
          options={isSkuData?.map((s) => ({
            value: s.inward_No,
            label: s.skU_No
          }))}
          placeholder="Select SKU"
        />
      )
    },
    // {
    //   key: "material_Id",
    //   label: "Material",
    //   w: "w-36",
    //   className: "align-top",
    //   render: (val, row, index) => (
    //     <InputField
    //       type="select"
    //       value={val || 0} 
    //       onChange={(v) => handleDetailChange(index, "material_Id", v)}
    //       options={isRawMaterialData?.map((m) => ({ value: m.id, label: m.name }))}
    //       placeholder="Select Material"
    //       required
    //     />
    //   )
    // },
    {
      key: "quantity",
      label: "Qty",
      w: "w-28",
      className: "align-top",
      render: (val, row, index) => (
        <InputField
          type="number"
          value={val}
          onChange={(v) => handleDetailChange(index, "quantity", v)}
          placeholder="0.00"
          required
        />
      )
    },
    {
      key: "unit_Id",
      label: "Unit",
      w: "w-32",
      className: "align-top",
      render: (val, row, index) => (
        <InputField
          type="select"
          value={val}
          onChange={(v) => handleDetailChange(index, "unit_Id", v)}
          options={isUnitData?.map((u) => ({ value: u.unit_Id, label: u.unit_Name }))}
          placeholder="Select Unit"
          required
        />
      )
    },
    {
      key: "rate",
      label: "Rate",
      w: "w-28",
      className: "align-top",
      render: (val, row, index) => (
        <InputField
          type="number"
          value={val}
          onChange={(v) => handleDetailChange(index, "rate", v)}
          placeholder="0.00"
          required
        />
      )
    },
    {
      key: "gsT_Per",
      label: "GST %",
      w: "w-24",
      className: "align-top",
      render: (val, row, index) => (
        <InputField
          type="number"
          value={val}
          onChange={(v) => handleDetailChange(index, "gsT_Per", v)}
          placeholder="18"
        />
      )
    },
    {
      key: "final_Amount",
      label: "Final Amt",
      w: "w-28",
      className: "align-top",
      render: (val) => (
        <div className="font-black text-gray-900 pt-2.5">
          {val?.toFixed(2) || "0.00"}
        </div>
      )
    },
    {
      key: "action",
      label: "Action",
      w: "w-16",
      align: "center",
      className: "align-top",
      render: (val, row, index) => (
        <div className="flex items-center justify-center gap-3 pt-2.5">
          <button
            type="button"
            onClick={addDetailItem}
            className="text-gray-400 hover:text-primary transition-colors"
            title="Add item"
          >
            <HiOutlinePlus size={18} />
          </button>
          <button
            type="button"
            onClick={() => removeDetailItem(index)}
            className="text-gray-400 hover:text-red-500 transition-colors"
            title="Remove item"
          >
            <MdDelete size={18} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden font-plus animate-in fade-in duration-500">
      <FormHeader
        title={isEditing ? "Edit Material Outward" : "Add Material Outward"}
        onCancel={onCancel}
      />
      <div className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar">
        <form className="space-y-5">
          {/* Section 1: Master Header */}
          <section className="relative overflow-hidden group">
            <div className="flex items-center gap-4 mb-4 border-b border-gray-100 pb-2">
              <span className="w-1.5 h-4 bg-primary"></span>
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[2.5px]">
                Basic Information
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label="Outward Date"
                type="date"
                name="outward_Date"
                value={formFields.outward_Date}
                onChange={(val) => handleChange("outward_Date", val)}
                required
              />
              <InputField
                label="Customer"
                type="selectWithAdd"
                name="customer_Id"
                value={formFields.customer_Id}
                onChange={(selected) => handleChange("customer_Id", selected?.value)}
                options={isCustomerData?.map((s) => ({
                  value: s.customer_Id,
                  label: s.customer_Name || s.name,
                }))}
                placeholder="Select Customer"
                onAddNewClick={() => setModalType("customer")}
                required
              />
            </div>

          </section>

          {/* Section 2: Material Details List */}
          <section className="relative overflow-hidden group">
            <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-2">
              <div className="flex items-center gap-4">
                <span className="w-1.5 h-4 bg-primary"></span>
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[2.5px]">
                  Material Items
                </h3>
              </div>

            </div>

            <div className="overflow-x-auto border border-gray-200 rounded-sm">
              <Table
                columns={detailColumns}
                data={formFields.details || []}
                showActions={false}
                noRecordsMessage="No items added. Click 'Add Item' to start."
                customFooter={customFooter}
                tableClassName="min-w-[1000px]"
              />
            </div>
          </section>

          <div className="grid grid-cols-1 gap-4 mt-4">
            <InputField
              label="Notes / Remark"
              type="textarea"
              name="notes"
              value={formFields.notes}
              onChange={(val) => handleChange("notes", val)}
              rows={2}
            />
          </div>
        </form>
      </div>

      <FormFooter
        onCancel={onCancel}
        onSubmit={(e) => handleSubmit(e, false)}
        submitText={isEditing ? "Update" : "Save"}
        isSaving={isSaving}
      >
        {!isEditing && (
          <button
            type="button"
            onClick={(e) => handleSubmit(e, true)}
            className="px-6 py-2 border border-primary text-primary font-black text-[10px] uppercase tracking-[2.5px] hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
            disabled={isSaving}
          >
            <MdOutlineAddLink size={14} /> Save & Add New
          </button>
        )}
      </FormFooter>

      {/* Internal Modals for Add New */}
      <Model
        isOpen={!!modalType}
        onClose={() => setModalType(null)}
        name={`Add ${modalType}`}
      >
        <EntityAddModal
          type={modalType}
          onClose={(newOption) => {
            setModalType(null);
            // If the user adds a new item, they can select it after refreshing or if the parent updates
          }}
        />
      </Model>
    </div>
  );
};

export default MaterialOutwardForm;
